import React from "react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";
import { TrustList } from "../components/TrustList";
import { MapPin, Flame, Home, Wrench, Zap, Thermometer, Droplet } from "lucide-react";
import { businessInfo, pageHeroImages } from "../data/mockData";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const services = [
  { Icon: Flame, label: "Boiler Servicing" },
  { Icon: Home, label: "Landlord Gas Safety Certificates (CP12)" },
  { Icon: Wrench, label: "Boiler Repairs" },
  { Icon: Zap, label: "Boiler Installations" },
  { Icon: Thermometer, label: "Heating Services" },
  { Icon: Droplet, label: "Plumbing Repairs" },
];

const AreasCoveredPage = () => {
  return (
    <>
      <Seo {...PAGE_SEO.areas} />
      <PageHero
        eyebrow="Areas Covered"
        image={pageHeroImages.areas}
        title="Gas Safe Engineer Covering Cardiff, Porth, Pontypridd, Bridgend &amp; Caerphilly"
        subtitle="Based in Porth, TOFT Gas, Heating & Plumbing provides boiler servicing in Cardiff, CP12 certificates in Bridgend, heating in Caerphilly and plumbing throughout Rhondda Cynon Taf — including Pontypridd, Tonypandy, Treorchy, Llantrisant, Talbot Green and Porthcawl. Trusted by homeowners, landlords, HMO owners and property investors."
      />

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-blue-soft border-l-4 border-brand-blue rounded-r-xl p-6 lg:p-8 mb-10">
            <p className="text-base sm:text-lg text-brand-navy leading-relaxed font-medium">
              We regularly provide <strong>boiler servicing in Cardiff</strong>,{" "}
              <strong>boiler repairs in Pontypridd</strong>,{" "}
              <strong>CP12 certificates in Bridgend</strong>,{" "}
              <strong>heating services in Caerphilly</strong> and{" "}
              <strong>plumbing repairs throughout Rhondda Cynon Taf</strong> — covering Porth,
              Treorchy, Tonypandy, Llantrisant, Talbot Green and Porthcawl too.
            </p>
          </div>

          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-4">
            Areas We Regularly Cover
          </h2>
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-7">
            We regularly provide boiler servicing, landlord gas safety certificates,
            boiler repairs, boiler installations and heating services across:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-7">
            {businessInfo.serviceTowns.map((t) => (
              <div
                key={t}
                data-testid={`area-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-3 hover:border-brand-blue hover:shadow-sm transition-all"
              >
                <MapPin className="h-4 w-4 text-brand-blue shrink-0" />
                <span className="text-sm font-bold text-brand-navy">{t}</span>
              </div>
            ))}
          </div>
          <p className="text-base text-slate-600 mb-2">and surrounding areas.</p>
          <p className="text-base text-slate-700">
            If you're unsure whether we cover your location, simply get in touch and
            we'll be happy to help.
          </p>
        </div>
      </section>

      <section className="bg-brand-slate-light py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-4">
            Services Available Throughout South Wales
          </h2>
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-7">
            Our most popular services include:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {services.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 bg-white rounded-lg p-4 border border-slate-200"
              >
                <div className="h-10 w-10 rounded-lg bg-brand-blue-soft text-brand-blue flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-base font-bold text-brand-navy">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-4">
            Local, Reliable & Trusted
          </h2>
          <div className="space-y-3 text-base sm:text-lg text-slate-700 leading-relaxed mb-10">
            <p>Choosing the right contractor isn't just about qualifications.</p>
            <p>
              You want somebody who communicates clearly, turns up when they say they
              will and provides honest advice.
            </p>
            <p className="font-semibold text-brand-navy">That's exactly what we aim to do.</p>
            <p>
              Much of our work comes from repeat customers and recommendations, and
              we're proud to support homeowners and landlords across South Wales year
              after year.
            </p>
          </div>

          <h3 className="text-xl sm:text-2xl font-black text-brand-navy mb-5">
            Why Choose TOFT?
          </h3>
          <TrustList
            items={[
              "Gas Safe Registered",
              "Trusted By Homeowners & Landlords",
              "Boiler Servicing From £70",
              "CP12 Certificates From £70",
              "Fast Turnaround",
              "Honest Advice",
              "Reliable Appointments",
              "Easy Contact By Phone, WhatsApp & Text",
            ]}
            columns={2}
          />
        </div>
      </section>

      <FinalCTA
        title="Not Sure If We Cover Your Area?"
        lines={[
          "If your town isn't listed, please get in touch.",
          "We regularly work throughout South Wales and may still be able to help.",
        ]}
      />
    </>
  );
};

export default AreasCoveredPage;
